import React from 'react';

import { Box, Card, Divider, Group, Stack, Text } from '@mantine/core';
import MovementTypeBadge from 'components/status-badges/MovementTypeBadge';
import { WHITEBOARD_SIZES } from 'components/Workout/whiteboardDisplay';
import ReadonlyWorkoutNotesRTE from 'components/workout-program/ReadonlyWorkoutNotesRTE';
import { MovementTypeEnum } from 'types/enums';
import { getAlphabetLetterByIndex } from 'utils/functions';

import PreviewMovementCard from './PreviewMovementCard';
import { PreviewMovementExtraRenderer, WorkoutTemplatePreviewSection } from './types';

interface PreviewSectionProps {
  section: WorkoutTemplatePreviewSection;
  index: number;
  enlarged?: boolean;
  renderMovementExtra?: PreviewMovementExtraRenderer;
}

const PreviewSection = ({
  section,
  index,
  enlarged = false,
  renderMovementExtra,
}: PreviewSectionProps): JSX.Element => {
  const sectionLetter = getAlphabetLetterByIndex(index);
  const hasMovements = section.movements && section.movements.length > 0;
  const isSectionLevel = section.scoreEntireSection === true;
  const sectionKind = (section.movements ?? []).some(
    m => m.workoutMovement?.movementType === MovementTypeEnum.BENCHMARK,
  )
    ? MovementTypeEnum.BENCHMARK
    : MovementTypeEnum.SCORED_SECTION;
  const badgeSize = enlarged ? WHITEBOARD_SIZES.sectionBadge.large : WHITEBOARD_SIZES.sectionBadge.compact;
  // Evaluated once: the letter turns green when any slot here is filled, and each card
  // takes its own.
  const movementExtras = (section.movements ?? []).map(movement => renderMovementExtra?.(movement) ?? null);
  const holdsExtra = movementExtras.some(Boolean);

  return (
    <div>
      <Group spacing={enlarged ? 'md' : 'xs'} align='center' mb='md'>
        <Box
          sx={theme => ({
            width: badgeSize,
            height: badgeSize,
            borderRadius: '50%',
            backgroundColor: holdsExtra ? theme.colors.green[6] : theme.colors.blue[6],
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: enlarged ? WHITEBOARD_SIZES.sectionBadgeFont : theme.fontSizes.sm,
            fontWeight: 600,
            flexShrink: 0,
          })}
        >
          {sectionLetter}
        </Box>
        <Text weight={600} size={enlarged ? WHITEBOARD_SIZES.sectionTitle : 'md'}>
          {section.name}
        </Text>
        {isSectionLevel && <MovementTypeBadge movementType={sectionKind} size={enlarged ? 'lg' : 'sm'} />}
      </Group>
      <Card
        p={enlarged ? 'xl' : 'md'}
        withBorder
        radius='md'
        sx={theme => ({ backgroundColor: theme.colors.gray[0] })}
      >
        {section.workoutInstructions?.trim() ? (
          <Box mb='md'>
            <ReadonlyWorkoutNotesRTE
              workoutNotes={section.workoutInstructions}
              mih={0}
              bordered={false}
              fontSize={enlarged ? WHITEBOARD_SIZES.bodyText : undefined}
            />
          </Box>
        ) : (
          <Text size={enlarged ? 'lg' : 'sm'} mb='md' italic color='dimmed'>
            No section description
          </Text>
        )}
        {hasMovements && (
          <>
            <Divider mb='md' />
            <Text weight={600} size={enlarged ? WHITEBOARD_SIZES.scoringLabel : 'sm'} mb='md'>
              Scoring
            </Text>
            <Stack spacing={enlarged ? 'sm' : 'xs'}>
              {section.movements.map((movement, movIndex) => (
                <PreviewMovementCard
                  key={movIndex}
                  movementName={isSectionLevel ? null : movement.workoutMovement?.name ?? ''}
                  scoreType={movement.scoreType}
                  enlarged={enlarged}
                  extra={movementExtras[movIndex]}
                />
              ))}
            </Stack>
          </>
        )}
      </Card>
    </div>
  );
};

export default PreviewSection;
